import {
    ENTBEHRLICHE_SPALTEN,
    spaltenSchluessel,
    type EntbehrlicheSpalte,
} from './queryColumns';

/**
 * DER SPALTEN-RUECKFALL — eine fehlende Zusatzspalte darf die Anwendung nicht
 * mehr toeten.
 *
 * ─── Der Vorfall ────────────────────────────────────────────────────────────
 * Migration Nr. 68 legt `project_services.transferred_from` an. Der Client
 * waehlte die Spalte mit, das SQL war nicht gelaufen. PostgREST antwortete mit
 * 42703 (undefined_column), der Drei-Versuche-Retry in
 * `doLoadProjectsFromCloud` gab nach rund zwei Sekunden auf und warf weiter —
 * KEIN EINZIGER NUTZER konnte mehr ein Projekt laden. Behoben nur durch
 * Deploy-Rollback und Nachziehen des SQL. Derselbe Fall war zuvor `min_hours`
 * (Nr. 31) und, auf TEST, `quantity_waived` (Nr. 50) samt `waived_entry_ids` (Nr. 70).
 *
 * ─── Die dritte Schicht ─────────────────────────────────────────────────────
 * Zwei Schichten verhindern den FEHLER: die CI-Wache
 * (`scripts/db/vorausgesetzteSpalten.ts`) und die Client-Wache
 * (`src/services/schemaVersion.ts`). Keine verhindert die WIRKUNG, wenn er
 * trotzdem passiert. Diese Datei ist die letzte Schicht und tut genau eines:
 * scheitert eine Abfrage an 42703 und ist die benannte Spalte in
 * `ENTBEHRLICHE_SPALTEN` verzeichnet, wird sie aus der Spaltenliste genommen
 * und die Abfrage EINMAL wiederholt.
 *
 * ─── Die vier Grenzen, und warum sie da sind ────────────────────────────────
 *  1. NUR bei 42703. Ein QUIC-Abbruch, ein 401, ein Rechtefehler sind etwas
 *     anderes und werden nicht angefasst. Umgekehrt gilt: 42703 ist KEIN
 *     Wiederholungsfall — dieselbe Abfrage ein zweites Mal zu schicken hilft
 *     dort nie, und der Aufrufer muss seinen eigenen Retry dafuer abbrechen
 *     (`istUnbekannteSpalte`).
 *  2. NUR entbehrliche Spalten (die additiv nachgeruesteten). Fehlt eine
 *     Urbestandsspalte, ist etwas grundlegend falsch, und dann soll es
 *     scheitern statt sich durchzumogeln.
 *  3. NIE raten. Laesst sich aus der Meldung keine Spalte lesen, oder ist die
 *     genannte nicht eindeutig zuzuordnen, wird nicht degradiert.
 *  4. TERMINIERT. Jede Spalte hoechstens einmal, dazu eine feste Obergrenze.
 *
 * ─── DIE WICHTIGSTE REGEL: der Rueckfall ist REIN LESEND ───────────────────
 * Wird eine Spalte beim Lesen weggelassen, ist ihr Wert im Speicher
 * `undefined`. Schriebe ein spaeterer Speichervorgang dieses `undefined` als
 * „leer" zurueck, haette die Notloesung DATEN VERNICHTET — schlimmer als der
 * Ausfall, den sie behebt. Diese Klasse Fehler hat das Projekt zweimal teuer
 * bezahlt (`jsonb_set`-Vorfall, `pruneOrphans`).
 *
 * Der Nachweis Spalte fuer Spalte steht bei `ohneFehlendeSpalten` weiter
 * unten; dort haengt auch der Riegel, der ihn traegt.
 */

/** PostgREST reicht den Postgres-Fehlercode durch: 42703 = undefined_column. */
export const UNDEFINED_COLUMN = '42703';

/** So sieht ein Fehler von supabase-js aus, soweit er hier gebraucht wird. */
export interface PostgrestFehlerRoh {
    code?: string | null;
    message?: string | null;
    details?: string | null;
    hint?: string | null;
}

/**
 * Ein geworfener PostgREST-Fehler, der seinen `code` BEHAELT.
 *
 * `doLoadProjectsFromCloud` warf bis zum 02.09.2026 `new Error(error.message)`
 * und damit den Code weg — genau die Angabe, an der sich 42703 von einem
 * QUIC-Abbruch unterscheiden laesst. Ohne sie kann kein Aufrufer die beiden
 * Faelle trennen, und der Retry drischt dreimal auf einen Fehler ein, der sich
 * durch Wiederholung nie aendert. Die `message` bleibt zeichengleich, damit
 * jede bestehende Fehleranzeige unveraendert weiterliest.
 */
export class PostgrestAbfrageFehler extends Error {
    readonly code: string;
    readonly details: string | null;
    readonly hint: string | null;

    constructor(roh: PostgrestFehlerRoh) {
        super(roh?.message || 'Abfrage fehlgeschlagen');
        this.name = 'PostgrestAbfrageFehler';
        this.code = typeof roh?.code === 'string' ? roh.code : '';
        this.details = typeof roh?.details === 'string' ? roh.details : null;
        this.hint = typeof roh?.hint === 'string' ? roh.hint : null;
    }
}

/** Macht aus der `error`-Antwort von supabase-js einen werfbaren Fehler. */
export const alsAbfrageFehler = (roh: unknown): PostgrestAbfrageFehler =>
    (roh instanceof PostgrestAbfrageFehler ? roh : new PostgrestAbfrageFehler((roh || {}) as PostgrestFehlerRoh));

const fehlerCode = (e: unknown): string => {
    const code = (e as { code?: unknown })?.code;
    return typeof code === 'string' ? code : '';
};

const fehlerMeldung = (e: unknown): string => {
    const message = (e as { message?: unknown })?.message;
    return typeof message === 'string' ? message : '';
};

/**
 * Ist das eine fehlende Spalte?
 *
 * Der Aufrufer braucht das fuer eine Entscheidung, die der Rueckfall selbst
 * nicht treffen kann: seinen EIGENEN Wiederholungsversuch abzubrechen. Ein
 * QUIC-Abbruch ist ein Wiederholungsfall, 42703 ist es nicht.
 */
export const istUnbekannteSpalte = (e: unknown): boolean => fehlerCode(e) === UNDEFINED_COLUMN;

/** Was aus einer PostgREST-Meldung herauszulesen war. */
export interface SpaltenBezug {
    /** unqualifiziert, so wie PostgREST sie nennt; fehlt, wenn die Meldung sie nicht nennt */
    tabelle?: string;
    spalte: string;
}

/**
 * Liest die Spalte aus der Fehlermeldung — und NUR aus ihr.
 *
 * PostgREST nennt sie im Klartext. Die Formen, die im Bestand vorkommen:
 *
 *   column project_services.transferred_from does not exist   (eingebettete Auswahl)
 *   column projects.foo does not exist                        (Haupttabelle)
 *   column "flat_rate" of relation "project_services" does not exist  (Schreibweg)
 *   column "min_hours" does not exist                         (blanke Postgres-Form)
 *
 * Ausdruecklich NICHT gelesen wird `hint` („Perhaps you meant to reference the
 * column …"): der Hinweis nennt eine ANDERE Spalte, und ihn zu verwenden waere
 * genau das Raten, das hier verboten ist.
 */
export const spalteAusMeldung = (meldung: unknown): SpaltenBezug | null => {
    if (typeof meldung !== 'string') return null;
    const text = meldung.trim();
    if (!text) return null;

    // Zuerst die Form mit ausdruecklicher Relation — sie traegt beide Angaben
    // getrennt und darf nicht von der allgemeineren Form verschluckt werden.
    const mitRelation = /column\s+"?([A-Za-z0-9_]+)"?\s+of\s+relation\s+"?([A-Za-z0-9_]+)"?\s+does not exist/i.exec(text);
    if (mitRelation) return { tabelle: mitRelation[2], spalte: mitRelation[1] };

    const einfach = /column\s+([A-Za-z0-9_."]+?)\s+does not exist/i.exec(text);
    if (!einfach) return null;

    // 'public.project_services.transferred_from' → Tabelle und Spalte sind die
    // beiden letzten Glieder; ein vorangestelltes Schema ist uninteressant.
    const teile = einfach[1].replace(/"/g, '').split('.').filter(Boolean);
    const spalte = teile[teile.length - 1];
    if (!spalte) return null;
    return teile.length > 1 ? { tabelle: teile[teile.length - 2], spalte } : { spalte };
};

/**
 * Ist die genannte Spalte entbehrlich — und ist die Zuordnung eindeutig?
 *
 * Nennt die Meldung eine Tabelle, muss sie passen. `flat_rate` gibt es
 * naemlich zweimal: auf `project_services` (Nr. 53) und auf `service_catalog`
 * (Nr. 55). Ohne diesen Abgleich wuerde ein Fehler der einen Tabelle die
 * Spalte der anderen aus der Liste nehmen.
 *
 * Nennt die Meldung KEINE Tabelle, wird nur degradiert, wenn der blosse Name
 * im Verzeichnis genau einmal vorkommt. Alles andere waere geraten.
 */
export const entbehrlicheSpalte = (bezug: SpaltenBezug | null): EntbehrlicheSpalte | null => {
    if (!bezug?.spalte) return null;

    const treffer = ENTBEHRLICHE_SPALTEN.filter((e) => e.spalte === bezug.spalte);
    if (treffer.length === 0) return null;

    if (bezug.tabelle) {
        const ohneSchema = (name: string): string => name.split('.').pop() || name;
        const passend = treffer.filter((e) => ohneSchema(e.tabelle) === ohneSchema(bezug.tabelle as string));
        return passend.length === 1 ? passend[0] : null;
    }

    return treffer.length === 1 ? treffer[0] : null;
};

/**
 * DIE ENTSCHEIDUNG — degradieren oder scheitern lassen? Rein, ohne Zustand.
 *
 * `bereitsBekannt` sind die Spalten, die schon aus der Liste genommen sind.
 * Kommt dieselbe Spalte ein zweites Mal, hat das Weglassen nicht geholfen —
 * dann ist etwas anderes im Spiel, und es wird nicht weiter probiert. Genau
 * diese Bedingung sorgt zusammen mit der Obergrenze in `mitSpaltenRueckfall`
 * dafuer, dass nichts endlos kreist.
 */
export const entscheideRueckfall = (
    fehler: unknown,
    bereitsBekannt: ReadonlySet<string>,
): EntbehrlicheSpalte | null => {
    if (!istUnbekannteSpalte(fehler)) return null;

    const spalte = entbehrlicheSpalte(spalteAusMeldung(fehlerMeldung(fehler)));
    if (!spalte) return null;
    if (bereitsBekannt.has(spaltenSchluessel(spalte.tabelle, spalte.spalte))) return null;

    return spalte;
};

// ═══════════════════════════════════════════════════════════════════════════
// DAS GEDAECHTNIS — was einmal fehlte, fehlt bis zum Neuladen
// ═══════════════════════════════════════════════════════════════════════════
//
// Ohne dieses Gedaechtnis liefe JEDE Folgeabfrage erneut in den Fehler und
// zahlte dafuer einen zusaetzlichen Rundlauf — bei einer seitenweise gebauten
// Projektliste einen je Seite. Der Zustand haelt nur die Laufzeit: nach einem
// Neuladen wird wieder die volle Liste versucht, denn inzwischen kann das SQL
// gelaufen sein. Nichts davon geht in den localStorage; ein dort vergessener
// Rueckfall waere unsichtbar und ueberlebte die Reparatur.

const fehlend = new Set<string>();
const hoerer = new Set<(stand: RueckfallStand | null) => void>();

/** Die Spalten, die diese Laufzeit als fehlend kennt — Schluessel `schema.tabelle.spalte`. */
export const fehlendeSpalten = (): ReadonlySet<string> => fehlend;

/** Was bisher weggelassen wurde, in der Reihenfolge des Auffallens. */
const weggelassen: EntbehrlicheSpalte[] = [];

/** Der anzeigbare Zustand; `null`, solange nichts fehlt. */
export interface RueckfallStand {
    spalten: EntbehrlicheSpalte[];
    /** fertiger Bannertext */
    text: string;
}

export const rueckfallStand = (): RueckfallStand | null => {
    if (weggelassen.length === 0) return null;

    const namen = weggelassen.map((s) => `${s.tabelle.replace(/^public\./, '')}.${s.spalte}`).join(', ');
    const nummern = [...new Set(weggelassen.map((s) => s.nummer))].sort((a, b) => a - b);
    return {
        spalten: [...weggelassen],
        text:
            `Notbetrieb: ${namen} fehlt in dieser Datenbank und wird uebergangen. `
            + `Fehlende Migration${nummern.length > 1 ? 'en' : ''}: Nr. ${nummern.join(', ')}. `
            + 'Die Anwendung laeuft weiter, zeigt diese Felder aber nicht und schreibt sie nicht. '
            + 'ERST das SQL fahren, DANN neu laden.',
    };
};

/**
 * Der Zustand muss ANZEIGBAR sein, nicht nur in der Konsole stehen.
 *
 * Ein stiller Rueckfall ist schlimmer als der Ausfall: den Ausfall bemerkt
 * jeder sofort, den Rueckfall niemand — und die Ursache bliebe monatelang
 * stehen. Angezeigt wird er auf demselben Weg wie die Schema-Wache: als
 * Warnbalken in `AdminView`, keine Sperre. Weil er erst WAEHREND einer
 * Abfrage entsteht (die Wache dagegen einmal beim Aufbau der Ansicht), reicht
 * ein einmaliges Lesen nicht — deshalb dieses Abonnement.
 *
 * Liefert die Abmeldefunktion, wie ein React-Effekt sie erwartet.
 */
export const abonniereRueckfall = (cb: (stand: RueckfallStand | null) => void): (() => void) => {
    hoerer.add(cb);
    return () => { hoerer.delete(cb); };
};

const melde = (spalte: EntbehrlicheSpalte, herkunft: string): void => {
    // LAUT, und mit allem, was zum Handeln noetig ist: welche Spalte, welche
    // Migration, welche Datei, welche Abfrage — und die Reihenfolge.
    console.error(
        [
            `[Spalten-Rueckfall] ${spalte.tabelle}.${spalte.spalte} gibt es in dieser Datenbank nicht.`,
            `  Angelegt von Migration Nr. ${spalte.nummer} (supabase/${spalte.datei}) — sie ist hier NICHT gelaufen.`,
            `  Aufgefallen bei: ${herkunft}`,
            '  Quelle der Spaltenliste: src/services/queryColumns.ts (ENTBEHRLICHE_SPALTEN)',
            `  FOLGE: ${spalte.folge}`,
            '  Die Spalte wird ab jetzt weder gelesen noch geschrieben — die Anwendung laeuft weiter,',
            '  aber sie laeuft im Notbetrieb. Das ist kein Dauerzustand.',
            '  ZU TUN: ERST das SQL fahren, DANN den Client neu laden. Nie umgekehrt.',
        ].join('\n'),
    );
};

/** Nimmt eine Spalte dauerhaft (fuer diese Laufzeit) aus allen Abfragen. */
export const merkeFehlendeSpalte = (spalte: EntbehrlicheSpalte, herkunft: string): void => {
    const schluessel = spaltenSchluessel(spalte.tabelle, spalte.spalte);
    if (fehlend.has(schluessel)) return;

    fehlend.add(schluessel);
    weggelassen.push(spalte);
    melde(spalte, herkunft);

    const stand = rueckfallStand();
    for (const h of hoerer) {
        // Ein Hoerer, der wirft, darf die Abfrage nicht mitreissen: sie ist
        // gerade dabei, den Ausfall zu ueberleben.
        try { h(stand); } catch { /* Anzeige ist nie wichtiger als der Ladepfad */ }
    }
};

/** Nur fuer Tests: der Laufzeitzustand wird zurueckgesetzt. */
export const setzeRueckfallZurueck = (): void => {
    fehlend.clear();
    weggelassen.length = 0;
};

/**
 * DIE OBERGRENZE.
 *
 * Mehr Versuche als es entbehrliche Spalten gibt kann es nicht brauchen: jede
 * wird hoechstens einmal weggelassen (`entscheideRueckfall` prueft das), und
 * mehr als alle gibt es nicht. Die Zahl steht trotzdem eigens da, damit ein
 * kuenftiger Umbau an `entscheideRueckfall` nicht versehentlich eine
 * Endlosschleife freilegt — zwei unabhaengige Riegel, nicht einer.
 */
export const MAX_RUECKFAELLE = ENTBEHRLICHE_SPALTEN.length;

/**
 * Fuehrt eine Abfrage aus und wiederholt sie ohne die fehlende Spalte.
 *
 * `abfrage` MUSS bei einem PostgREST-Fehler werfen (ueber `alsAbfrageFehler`,
 * damit der `code` erhalten bleibt) und die aktuelle Liste aus
 * `fehlendeSpalten()` verwenden — sonst fragt der zweite Versuch dasselbe noch
 * einmal.
 *
 * Alles, was NICHT als Rueckfall taugt, fliegt unveraendert heraus: ein
 * anderer Fehlercode, eine unlesbare Meldung, eine nicht entbehrliche Spalte,
 * eine schon weggelassene Spalte. Der Aufrufer sieht dann genau das Verhalten
 * von vorher.
 */
export const mitSpaltenRueckfall = async <T>(
    herkunft: string,
    abfrage: () => Promise<T>,
): Promise<T> => {
    for (let versuch = 0; ; versuch++) {
        try {
            return await abfrage();
        } catch (e) {
            const spalte = entscheideRueckfall(e, fehlend);
            if (!spalte) throw e;
            if (versuch >= MAX_RUECKFAELLE) {
                console.error(
                    `[Spalten-Rueckfall] Obergrenze von ${MAX_RUECKFAELLE} Rueckfaellen erreicht `
                    + `(${herkunft}) — es wird nicht weiter degradiert.`,
                );
                throw e;
            }
            merkeFehlendeSpalte(spalte, herkunft);
        }
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// DER SCHREIBWEG — der Riegel gegen den Datenverlust
// ═══════════════════════════════════════════════════════════════════════════
//
// ─── Die Prüfung, Spalte für Spalte ────────────────────────────────────────
// Geprueft wurde JEDER Weg, der `public.project_services` schreibt. Die Frage
// war jedes Mal dieselbe: kann eine beim LESEN weggelassene Spalte durch ein
// spaeteres Speichern GELEERT werden?
//
//  1. `min_hours` (Nr. 31) — DAS MUSTER TRAEGT NICHT, und zwar mit Absicht.
//     `saveProjectToCloud` sendet den Schluessel IMMER, leere Angabe als
//     ausdrueckliches `null`; die Begruendung steht dort und bleibt richtig:
//     das Feld ist im Dialog leerbar, und `save_project_children_tx` baut
//     seine UPDATE-Spalten aus `jsonb_object_keys` — ein ausgelassener
//     Schluessel wird nie aktualisiert, ein geleertes Feld haette den alten
//     Wert stehen lassen. Genau diese Eigenschaft macht den Rueckfall hier
//     aber toedlich: nicht gelesen → `minHours === undefined` → als `null`
//     geschrieben → die Mindeststunden des ganzen Bestands weg, sobald jemand
//     aus einer Liste speichert. Deshalb dieser Riegel: fehlt die Spalte in
//     dieser Datenbank, wird ihr Schluessel entfernt. Sie kann dann nicht
//     geleert werden — und geschrieben werden koennte sie ohnehin nicht, denn
//     es gibt sie nicht.
//  2. `quantity_waived`, `waived_reason`, `waived_at`, `waived_by` (Nr. 50)
//     und `waived_entry_ids` (Nr. 70) — TRAEGT. `saveProjectToCloud` setzt die
//     fuenf Schluessel nur, wenn `s.quantityWaived !== undefined`. Nicht
//     gelesen heisst `undefined` heisst kein Schluessel heisst kein UPDATE.
//     Der Riegel unten ist hier die zweite Linie, nicht die erste.
//  3. `flat_rate` (Nr. 53) — TRAEGT, gleiches Muster (`s.flatRate === undefined`).
//  4. `transferred_from` (Nr. 68) — TRAEGT, gleiches Muster
//     (`s.transferredFrom === undefined`).
//  5. `bestandteile` (Nr. 165) — TRAEGT, und strenger als das Muster:
//     nicht gelesen heisst, die Abbildung (`projectLv.bestandteilFelderAusZeile`)
//     setzt das Feld gar nicht, und ohne Feld kein Schluessel. Aber auch ein
//     gelesener Wert geht nur mit, wenn die Sitzung ihn gegenueber ihrem
//     Ladestand geaendert hat oder die Position neu ist
//     (cloudService.definitionSenden, Befund F1).
//  6. `bestandteile_offen` (Nr. 165) — TRAEGT TRIVIAL: die Spalte pflegt die
//     DATENBANK (Trigger aus Nr. 166), `saveProjectToCloud` sendet sie NIE,
//     auch nicht in den geretteten Zeilen aus `fehlendeUmgezogeneRows`. Fehlt
//     sie beim Lesen, ist das Gate einer Paketposition unbekannt und sie gilt
//     als gesperrt — die sichere Richtung.
//  7. `stunden_kontingent` (Nr. 171) — TRAEGT, Muster `flat_rate`, und
//     zusaetzlich von der Datenbank gehalten: nicht gelesen heisst, die
//     Abbildung (`projectLv.stundenFelderAusZeile`) setzt das Feld nicht, und
//     `saveProjectToCloud` sendet den Schluessel nur bei
//     `s.stundenKontingent !== undefined`. Selbst ein gelesener, inzwischen
//     veralteter Wert setzt nichts zurueck: bei einer BESTEHENDEN Position
//     behaelt der Trigger aus Nr. 172 den alten Wert (S10), aendern geht nur
//     ueber die Admin-RPC `position_stundenkontingent_setzen`. Wirksam wird
//     der gesendete Wert allein beim Anlegen (Kopie aus Katalog/Angebot).
//  8. `stunden_erfasst` (Nr. 171) — TRAEGT TRIVIAL wie `bestandteile_offen`:
//     die Summe pflegt die DATENBANK (Trigger aus Nr. 172), `saveProjectToCloud`
//     sendet sie NIE, auch nicht in den geretteten Zeilen. Fehlt sie beim
//     Lesen, sind die erfassten Stunden unbekannt — gesperrt wird dadurch
//     nichts, die Stunden sind rein intern (S8).
//
// ─── Die Wege, die nicht ueber `saveProjectToCloud` gehen ──────────────────
//  • `applyLinkTransfer` schreibt `transferred_from` in demselben UPDATE, das
//    `project_id` umhaengt; `applyLinkReturn` setzt es auf `null`. Beides ist
//    eine AUSDRUECKLICHE Absicht des Aufrufers, kein Zurueckschreiben eines
//    gelesenen Werts — und beides scheitert laut, wenn es die Spalte nicht
//    gibt. Das ist richtig so: ohne die Spalte kann der Umzug seine Herkunft
//    nicht festhalten, und ein stiller Umzug ohne Herkunft ist genau der
//    Zustand, gegen den Nr. 68 gebaut wurde. `bestandteile` fasst keiner der
//    beiden an: die Definition steht an der Zeile und wandert mit ihr, die
//    Eintraege ziehen per Fremdschluessel (`on update cascade`) nach. Ebenso
//    `stunden_kontingent`/`stunden_erfasst` (Nr. 171) und das Stundenprotokoll.
//  • `TasksView` schreibt `is_optional`, `ProjectSupplementModal` loescht,
//    `projectQuietService` und `SigePlanModul` lesen nur, `applyLinkTransfer`
//    setzt ausserdem `quantity_contracted` und `project_id`. Keine dieser
//    Stellen fasst eine entbehrliche Spalte an.
//  • `mapServicesToDb` (listenSpiegel.ts) baut nur den Spiegel der Listenzeile IM SPEICHER
//    (types.ts:487: „NIE in die Datenbank"). Er kann nichts leeren.
//  • `serviceWaiverService`, `flatRateSwitchService`, `manualBillingService`,
//    `serviceReversalService` laden mit `loadProjectById` und speichern ueber
//    `saveProjectToCloud` — sie erben damit genau die vier Faelle oben.
//
// ─── Warum `loadProjectById` ausserhalb steht ──────────────────────────────
// Dieser Pfad liest `select('*')` — auf beiden Tabellen. Ein `*` kann 42703
// gar nicht ausloesen: PostgREST liefert, was da ist. Der Pfad braucht keinen
// Rueckfall und bekommt auch keinen; er ist aber zugleich der Grund, weshalb
// der Riegel unten nicht am Lesen, sondern am SCHREIBEN haengt — auch ohne
// jeden Rueckfall im Lesepfad ist `s.minHours` hier `undefined`, sobald die
// Spalte fehlt.

/**
 * Nimmt die als fehlend bekannten Spalten aus einer Schreibzeile.
 *
 * Im Normalbetrieb (nichts fehlt) gibt sie die Zeile UNVERAENDERT zurueck —
 * dasselbe Objekt, kein Kopieren, kein Schluessel weniger. Der Riegel ist
 * ausschliesslich fuer den Notbetrieb da und darf sonst nichts bewirken.
 */
export const ohneFehlendeSpalten = <T extends Record<string, unknown>>(
    tabelle: string,
    zeile: T,
): T => {
    if (fehlend.size === 0) return zeile;

    const zuEntfernen = Object.keys(zeile).filter((k) => fehlend.has(spaltenSchluessel(tabelle, k)));
    if (zuEntfernen.length === 0) return zeile;

    const kopie = { ...zeile };
    for (const k of zuEntfernen) delete kopie[k];
    return kopie;
};
